<template>
    <div class="view-inner">

        <!-- Encabezado: qué es la pantalla y qué esperar -->
        <div class="page-header">
            <div>
                <h1 class="page-header-title"><i class="bi bi-person-badge"></i> Datos de Agente</h1>
                <p class="page-header-sub">
                    Solicita los datos (nombres y DNI) del agente de transporte asignado a un envío.
                    Recibirás la respuesta por correo, normalmente en menos de una hora.
                </p>
            </div>
        </div>

        <!-- 1. Nueva solicitud -->
        <section class="agente-card mb-4">
            <header class="agente-card-header">
                <h2 class="agente-card-title"><i class="bi bi-pencil-square"></i> Nueva solicitud</h2>
            </header>
            <div class="agente-card-body">
                <form @submit.prevent="guardar" class="agente-form" novalidate>

                    <!-- Tipo gestión -->
                    <div class="form-block full">
                        <label class="form-lbl">Tipo de gestión <span class="req">*</span></label>
                        <div class="tipo-gestion-chips" role="radiogroup" aria-label="Tipo de gestión">
                            <label class="tipo-chip" :class="{ active: form.tipoGestion === 'MERCADERIA' }">
                                <input type="radio" value="MERCADERIA" v-model="form.tipoGestion" name="tipoGestion" />
                                <i class="bi bi-box-seam"></i> Mercadería
                            </label>
                            <label class="tipo-chip" :class="{ active: form.tipoGestion === 'DOCUMENTOS' }">
                                <input type="radio" value="DOCUMENTOS" v-model="form.tipoGestion" name="tipoGestion" />
                                <i class="bi bi-file-earmark-text"></i> Documentos
                            </label>
                        </div>
                    </div>

                    <!-- RUC: Enter busca la empresa (no envía el formulario) -->
                    <div class="form-block">
                        <label class="form-lbl" for="agenteRuc">RUC de la empresa <span class="req">*</span></label>
                        <div class="input-with-actions">
                            <input id="agenteRuc" type="text" inputmode="numeric" maxlength="11" class="form-control"
                                v-model.trim="form.ruc" placeholder="Ej: 20123456789"
                                @keydown.enter.prevent="getEmpresa()" />
                            <button type="button" class="fab-btn fab-search" @click="getEmpresa()"
                                :disabled="isLoadingEmpresa || !form.ruc" title="Buscar empresa por RUC" aria-label="Buscar empresa por RUC">
                                <span v-if="isLoadingEmpresa" class="spinner-border spinner-border-sm"></span>
                                <i v-else class="bi bi-search"></i>
                            </button>
                            <button type="button" class="fab-btn fab-add" @click="agregarEmpresa()"
                                title="Registrar una empresa nueva" aria-label="Registrar una empresa nueva">
                                <i class="bi bi-plus-lg"></i>
                            </button>
                        </div>
                        <small v-if="rucNoEncontrado" class="form-help form-help-error">
                            No encontramos una empresa con ese RUC. Revisa el número o regístrala con el botón <strong>+</strong>.
                        </small>
                        <small v-else-if="!form.empresaId" class="form-help">Escribe el RUC y presiona Enter o la lupa.</small>
                    </div>

                    <div class="form-block">
                        <label class="form-lbl">Razón social</label>
                        <input type="text" class="form-control form-readonly" :value="form.razonSocial" readonly
                            placeholder="Se completa al buscar el RUC" tabindex="-1" />
                    </div>

                    <!-- Cliente final: con buscador (hay empresas con cientos de sucursales) -->
                    <div class="form-block">
                        <label class="form-lbl" for="agenteCliente">Cliente final <span class="req">*</span></label>
                        <input v-if="sucursales.length > 8" type="search" class="form-control form-control-sm mb-1"
                            v-model.trim="busquedaCliente" :disabled="!sucursales.length"
                            placeholder="Buscar sucursal por nombre…" aria-label="Buscar cliente final" />
                        <select id="agenteCliente" class="form-select" v-model="form.clienteFinal" :disabled="!sucursales.length">
                            <option :value="null" disabled>
                                {{ sucursales.length ? `Seleccione (${sucursalesFiltradas.length} de ${sucursales.length})` : 'Primero busca el RUC' }}
                            </option>
                            <option v-for="item in sucursalesFiltradas" :key="item.id" :value="item">{{ item.nombre }}</option>
                        </select>
                    </div>

                    <div class="form-block">
                        <label class="form-lbl" for="agenteArea">Área solicitante <span class="req">*</span></label>
                        <select id="agenteArea" class="form-select" v-model="form.cod_metodo_envio_id">
                            <option value="" disabled>Seleccione área</option>
                            <option v-for="item in codigosMetodoEnvio" :key="item.id" :value="item.id">{{ item.codigo }}</option>
                        </select>
                    </div>

                    <div class="form-block full">
                        <label class="form-lbl">Dirección del cliente final</label>
                        <input readonly :value="direccionCliente" type="text" class="form-control form-readonly"
                            placeholder="Se completa al elegir el cliente final" tabindex="-1" />
                    </div>

                    <div class="form-block">
                        <label class="form-lbl" for="agenteSolicitante">Solicitante <span class="req">*</span></label>
                        <input id="agenteSolicitante" type="text" class="form-control" v-model.trim="form.solicitante" placeholder="Nombre de quien solicita" />
                    </div>

                    <div class="form-block">
                        <label class="form-lbl">¿Qué datos del agente necesitas?</label>
                        <div class="requisitos-wrap">
                            <label class="req-chip" :class="{ active: form.nombresApellidos }">
                                <input type="checkbox" v-model="form.nombresApellidos" />
                                <i class="bi bi-person-check"></i> Nombres y apellidos
                            </label>
                            <label class="req-chip" :class="{ active: form.dni }">
                                <input type="checkbox" v-model="form.dni" />
                                <i class="bi bi-credit-card"></i> DNI
                            </label>
                        </div>
                    </div>

                    <div class="form-block">
                        <label class="form-lbl" for="agenteObs">Observaciones</label>
                        <textarea id="agenteObs" class="form-control" rows="2" v-model="form.observaciones" placeholder="Instrucciones especiales, número de rastreo, referencias…"></textarea>
                    </div>

                    <div class="form-block">
                        <label class="form-lbl" for="agenteDocs">Documentos extras</label>
                        <input id="agenteDocs" class="form-control" type="text" v-model="form.documentosExtras" placeholder="Ej: SCTR, placa vehicular, DNI en PDF…" />
                    </div>

                    <div class="form-block full form-submit">
                        <button class="btn-guardar" type="submit" :disabled="isSavingForm || isLoadingEmpresa">
                            <span v-if="isSavingForm" class="spinner-border spinner-border-sm me-2"></span>
                            <i v-else class="bi bi-send me-2"></i>
                            {{ isSavingForm ? 'Enviando…' : 'Enviar solicitud' }}
                        </button>
                    </div>
                </form>
            </div>
        </section>

        <!-- 2. Historial: siempre visible, lo más reciente primero -->
        <section ref="historial">
            <h2 class="seccion-titulo"><i class="bi bi-clock-history"></i> Historial de solicitudes</h2>
            <div class="table-responsive">
                <table class="table table-hover">
                    <thead>
                        <tr>
                            <th>Fecha</th>
                            <th>Estado</th>
                            <th>Tipo</th>
                            <th>Cliente final</th>
                            <th>Dpto.</th>
                            <th>Provincia</th>
                            <th>Distrito</th>
                            <th>Solicitante</th>
                            <th>RUC</th>
                            <th class="text-center">Nombres</th>
                            <th class="text-center">DNI</th>
                            <th>Observaciones</th>
                            <th class="col-acciones">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in paginar(solicitudesOrdenadas)" :key="item.id" :class="{ 'fila-nueva': item.id === idRecienCreado }">
                            <td class="celda-id">{{ formatearFecha(item.created_at) }}</td>
                            <td>
                                <span class="badge-doc" :class="item.has_respuesta ? 'badge-doc-vigente' : 'badge-doc-pronto'">
                                    {{ item.has_respuesta ? 'Respondida' : 'Pendiente' }}
                                </span>
                            </td>
                            <td>
                                <span class="badge-doc" :class="item.tipo_gestion === 'MERCADERIA' ? 'badge-tipo-merc' : 'badge-tipo-doc'">
                                    {{ item.tipo_gestion === 'MERCADERIA' ? 'Mercadería' : 'Documentos' }}
                                </span>
                            </td>
                            <td class="celda-entidad"><div class="entidad-nombre" :title="item.sucursal_nombre">{{ item.sucursal_nombre }}</div></td>
                            <td>{{ item.departamento_nombre }}</td>
                            <td>{{ item.provincia_nombre }}</td>
                            <td>{{ item.distrito_nombre }}</td>
                            <td class="celda-entidad"><div class="entidad-nombre" :title="item.solicitante">{{ item.solicitante }}</div></td>
                            <td class="celda-id">{{ item.ruc || '—' }}</td>
                            <td class="text-center">{{ item.nombres_apellidos ? 'Sí' : 'No' }}</td>
                            <td class="text-center">{{ item.dni ? 'Sí' : 'No' }}</td>
                            <td class="celda-entidad"><div class="entidad-nombre" :title="item.observaciones">{{ item.observaciones || '—' }}</div></td>
                            <td class="col-acciones">
                                <button class="btn-icon btn-icon-view" @click="abrirModalRespuesta(item)" :disabled="isLoadingRespuesta"
                                    :title="item.has_respuesta ? 'Ver respuesta' : (esAdmin() ? 'Responder' : 'Ver solicitud')"
                                    :aria-label="item.has_respuesta ? 'Ver respuesta' : (esAdmin() ? 'Responder' : 'Ver solicitud')">
                                    <i :class="item.has_respuesta || !esAdmin() ? 'bi bi-eye' : 'bi bi-reply'"></i>
                                </button>
                                <button v-if="esAdmin()" class="btn-icon btn-icon-del" @click="eliminarSolicitud(item)"
                                    title="Eliminar solicitud" aria-label="Eliminar solicitud" :disabled="isDeletingSolicitudId === item.id">
                                    <i class="bi bi-trash"></i>
                                </button>
                            </td>
                        </tr>
                        <tr v-if="!solicitudes.length" class="empty-row">
                            <td colspan="13">Aún no hay solicitudes. Las que envíes aparecerán aquí.</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <PaginacionListado :pagina="paginaActualDe(solicitudesOrdenadas)" :total-paginas="totalPaginasDe(solicitudesOrdenadas)"
                :total="solicitudesOrdenadas.length" :por-pagina="porPagina" :en-pagina="paginar(solicitudesOrdenadas).length"
                etiqueta="solicitudes" @cambiar="pagina = $event" />
        </section>

        <!-- Modal de respuesta: el admin responde; el solicitante solo consulta -->
        <div v-if="showRespuestaModal" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5); z-index:1070;">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">
                            <i class="bi bi-chat-left-text me-2"></i>
                            {{ esAdmin() ? (solicitudSeleccionada.has_respuesta ? 'Respuesta de la solicitud' : 'Responder solicitud') : 'Detalle de la solicitud' }}
                        </h5>
                        <button type="button" class="btn-close" @click="cerrarModalRespuesta()" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">

                        <div class="solicitud-resumen mb-4">
                            <div class="solicitud-resumen-grid">
                                <div class="resumen-item"><span class="resumen-label">Tipo de gestión</span><span class="resumen-val">{{ solicitudSeleccionada.tipo_gestion }}</span></div>
                                <div class="resumen-item"><span class="resumen-label">Fecha</span><span class="resumen-val">{{ formatearFecha(solicitudSeleccionada.created_at) }}</span></div>
                                <div class="resumen-item"><span class="resumen-label">RUC</span><span class="resumen-val">{{ solicitudSeleccionada.ruc || '—' }}</span></div>
                                <div class="resumen-item"><span class="resumen-label">Cliente final</span><span class="resumen-val">{{ solicitudSeleccionada.sucursal_nombre }}</span></div>
                                <div class="resumen-item"><span class="resumen-label">Ubicación</span><span class="resumen-val">{{ [solicitudSeleccionada.departamento_nombre, solicitudSeleccionada.provincia_nombre, solicitudSeleccionada.distrito_nombre].filter(Boolean).join(' / ') }}</span></div>
                                <div class="resumen-item"><span class="resumen-label">Solicitante</span><span class="resumen-val">{{ solicitudSeleccionada.solicitante }}</span></div>
                                <div class="resumen-item"><span class="resumen-label">Datos solicitados</span><span class="resumen-val">{{ datosSolicitadosTexto(solicitudSeleccionada) }}</span></div>
                                <div class="resumen-item"><span class="resumen-label">Documentos extras</span><span class="resumen-val">{{ solicitudSeleccionada.documentos_extras || '—' }}</span></div>
                                <div class="resumen-item resumen-full"><span class="resumen-label">Observaciones</span><span class="resumen-val">{{ solicitudSeleccionada.observaciones || '—' }}</span></div>
                            </div>
                        </div>

                        <!-- Solicitante sin respuesta todavía -->
                        <div v-if="!esAdmin() && !solicitudSeleccionada.has_respuesta" class="aviso-pendiente">
                            <i class="bi bi-hourglass-split"></i>
                            <div>
                                <strong>Tu solicitud está pendiente.</strong><br>
                                Te avisaremos por correo cuando ANW cargue los datos del agente.
                            </div>
                        </div>

                        <template v-else>
                            <div class="seccion-titulo-modal mb-3"><i class="bi bi-person-vcard me-2"></i>Datos del agente</div>
                            <div class="row g-3 mb-4">
                                <div class="col-md-6">
                                    <label class="form-label form-label-sm" for="respNombres">Nombres y apellidos <span v-if="esAdmin() && solicitudSeleccionada.nombres_apellidos" class="req">*</span></label>
                                    <input id="respNombres" type="text" class="form-control form-control-sm" v-model="respuesta.nombres_apellidos"
                                        :readonly="!esAdmin()" :class="{ 'form-readonly': !esAdmin() }" placeholder="Nombres y apellidos del agente" />
                                </div>
                                <div class="col-md-6">
                                    <label class="form-label form-label-sm" for="respDni">DNI <span v-if="esAdmin() && solicitudSeleccionada.dni" class="req">*</span></label>
                                    <input id="respDni" type="text" inputmode="numeric" maxlength="12" class="form-control form-control-sm" v-model="respuesta.dni"
                                        :readonly="!esAdmin()" :class="{ 'form-readonly': !esAdmin() }" placeholder="DNI del agente" />
                                </div>
                            </div>

                            <div class="seccion-titulo-modal mb-3"><i class="bi bi-paperclip me-2"></i>Archivos adjuntos</div>
                            <div v-if="esAdmin()" class="archivo-upload-row mb-3">
                                <input type="file" class="form-control form-control-sm" ref="fileInput" @change="seleccionarArchivo" aria-label="Elegir archivo" />
                                <button class="btn btn-sm btn-outline-primary" @click="subirArchivo()" :disabled="isUploadingArchivo || !nuevoArchivo.archivo">
                                    <i class="bi bi-upload me-1"></i>{{ isUploadingArchivo ? 'Subiendo…' : 'Subir archivo' }}
                                </button>
                            </div>
                            <div class="table-responsive">
                                <table class="table table-hover">
                                    <thead>
                                        <tr>
                                            <th>Documento</th>
                                            <th>Fecha de subida</th>
                                            <th class="col-acciones">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(archivo, index) in archivos" :key="archivo.id">
                                            <td class="celda-entidad"><div class="entidad-nombre" :title="archivo.nombre">{{ archivo.nombre }}</div></td>
                                            <td class="celda-id">{{ formatearFecha(archivo.created_at) }}</td>
                                            <td class="col-acciones">
                                                <button class="btn-icon btn-icon-view" @click="verArchivo(archivo)" title="Ver archivo" aria-label="Ver archivo">
                                                    <i class="bi bi-eye"></i>
                                                </button>
                                                <button v-if="esAdmin()" class="btn-icon btn-icon-del" @click="eliminarArchivo(archivo.id, index)"
                                                    title="Eliminar archivo" aria-label="Eliminar archivo" :disabled="isDeletingArchivoId === archivo.id">
                                                    <i class="bi bi-trash"></i>
                                                </button>
                                            </td>
                                        </tr>
                                        <tr v-if="!archivos.length" class="empty-row">
                                            <td colspan="3">No hay archivos adjuntos.</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </template>
                    </div>
                    <div class="modal-footer">
                        <button class="btn btn-secondary" @click="cerrarModalRespuesta()">Cerrar</button>
                        <button v-if="esAdmin()" class="btn btn-primary" @click="guardarRespuesta()" :disabled="isSavingRespuesta">
                            <span v-if="isSavingRespuesta" class="spinner-border spinner-border-sm me-2"></span>
                            {{ isSavingRespuesta ? 'Enviando…' : 'Guardar y avisar al solicitante' }}
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modal de empresa (registrar empresa nueva) -->
        <div v-if="showCompanyModal" class="modal fade show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5); z-index:1060;">
            <div class="modal-dialog modal-xl modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title"><i class="bi bi-building me-2"></i>Gestión de clientes</h5>
                        <button type="button" class="btn-close" @click="cerrarModalEmpresa()" aria-label="Cerrar"></button>
                    </div>
                    <div class="modal-body">
                        <p class="form-help mb-3">Registra la empresa y sus sucursales. Al cerrar, buscaremos de nuevo el RUC del formulario.</p>
                        <CompanyPage />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
import CompanyPage from '@/components/CompanyModal.vue'
import PaginacionListado from '@/components/PaginacionListado.vue'
import paginacionLocal from '@/mixins/paginacionLocal'
import Swal from 'sweetalert2'
import axios from 'axios'

const formularioVacio = () => ({
    tipoGestion: '',
    ruc: '',
    empresaId: '',
    razonSocial: '',
    cod_metodo_envio_id: '',
    nombresApellidos: false,
    dni: false,
    clienteFinal: null,
    documentosExtras: '',
    observaciones: '',
    solicitante: ''
});

export default {
    name: 'DatosAgentePage',
    components: { CompanyPage, PaginacionListado },
    mixins: [paginacionLocal],
    data() {
        return {
            user: {},
            sucursales: [],
            busquedaCliente: '',
            rucBuscado: '',
            rucNoEncontrado: false,
            solicitudes: [],
            idRecienCreado: null,
            codigosMetodoEnvio: [],
            showCompanyModal: false,
            showRespuestaModal: false,
            solicitudSeleccionada: {},
            respuesta: {},
            archivos: [],
            isSavingForm: false,
            isSavingRespuesta: false,
            isLoadingEmpresa: false,
            isLoadingRespuesta: false,
            isUploadingArchivo: false,
            isDeletingArchivoId: null,
            isDeletingSolicitudId: null,
            nuevoArchivo: { archivo: null, nombre: '' },
            form: formularioVacio(),
        };
    },
    computed: {
        // Lo más reciente primero
        solicitudesOrdenadas() {
            return [...this.solicitudes].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        },
        sucursalesFiltradas() {
            const q = this.busquedaCliente.toLowerCase();
            return q ? this.sucursales.filter(s => (s.nombre || '').toLowerCase().includes(q)) : this.sucursales;
        },
        direccionCliente() {
            const c = this.form.clienteFinal;
            if (!c) return '';
            return [c.direccion, c.distrito_nombre, c.provincia_nombre, c.departamento_nombre].filter(Boolean).join(', ');
        },
    },
    watch: {
        // Si cambia el RUC después de buscar, los datos de la empresa anterior ya no valen
        'form.ruc'(nuevo) {
            if (this.rucBuscado && nuevo !== this.rucBuscado) this.limpiarEmpresa();
            this.rucNoEncontrado = false;
        },
    },
    mounted() {
        this.user = JSON.parse(localStorage.getItem("user")) || {};
        this.form.solicitante = this.nombreUsuario();
        this.getCodigoMetodoEnvio();
        this.getDatosAgente();
    },
    methods: {
        esAdmin() {
            return (this.user?.cargo || '').toLowerCase() === 'admin';
        },
        nombreUsuario() {
            return [this.user.name, this.user.apellido].filter(Boolean).join(' ');
        },
        formatearFecha(fecha) {
            return fecha ? new Date(fecha).toLocaleDateString('es-PE') : '—';
        },
        datosSolicitadosTexto(s) {
            const datos = [s.nombres_apellidos && 'Nombres y apellidos', s.dni && 'DNI'].filter(Boolean);
            return datos.length ? datos.join(' y ') : '—';
        },
        // Mensaje legible a partir de un error de validación de Laravel
        mensajeError(error, porDefecto) {
            const d = error && error.response && error.response.data;
            if (d && d.errors) return Object.values(d.errors).flat().join(' ');
            return (d && d.message) || porDefecto;
        },

        // ── Empresa y cliente final ──
        limpiarEmpresa() {
            this.form.empresaId = '';
            this.form.razonSocial = '';
            this.form.clienteFinal = null;
            this.sucursales = [];
            this.busquedaCliente = '';
            this.rucBuscado = '';
        },
        async getEmpresa() {
            if (this.isLoadingEmpresa || !this.form.ruc) return;
            this.isLoadingEmpresa = true;
            this.limpiarEmpresa();
            this.rucNoEncontrado = false;
            try {
                const response = await axios.get('empresa-by-ruc/' + this.form.ruc);
                if (!response.data || !response.data.id) {
                    this.rucNoEncontrado = true;
                    return;
                }
                this.form.empresaId = response.data.id;
                this.form.razonSocial = response.data.nombre;
                this.rucBuscado = this.form.ruc;
                await this.getSucursales();
            } catch (error) {
                this.rucNoEncontrado = true;
            } finally {
                this.isLoadingEmpresa = false;
            }
        },
        async getSucursales() {
            try {
                const response = await axios.get('empresa-sucursales/' + this.form.empresaId);
                this.sucursales = response.data || [];
                if (this.sucursales.length === 1) this.form.clienteFinal = this.sucursales[0];
            } catch (error) {
                console.error('Error cargando sucursales:', error);
            }
        },
        agregarEmpresa() {
            this.showCompanyModal = true;
        },
        cerrarModalEmpresa() {
            this.showCompanyModal = false;
            if (this.form.ruc) this.getEmpresa();
        },
        async getCodigoMetodoEnvio() {
            try {
                const response = await axios.get('cod-metodo-envio');
                this.codigosMetodoEnvio = response.data;
            } catch (error) {
                console.error('Error cargando áreas:', error);
            }
        },

        // ── Solicitudes ──
        async getDatosAgente() {
            try {
                const response = await axios.get("/datos-agente/list");
                this.solicitudes = response.data || [];
            } catch (error) {
                console.error("Error al obtener solicitudes:", error);
            }
        },
        async guardar() {
            if (this.isSavingForm) return;
            const faltan = [
                !this.form.tipoGestion && 'tipo de gestión',
                !this.form.empresaId && 'RUC de la empresa (búscalo con la lupa)',
                !this.form.clienteFinal && 'cliente final',
                !this.form.cod_metodo_envio_id && 'área solicitante',
                !this.form.solicitante && 'solicitante',
            ].filter(Boolean);
            if (faltan.length) {
                Swal.fire({ title: 'Faltan datos', html: 'Completa: <strong>' + faltan.join(', ') + '</strong>.', icon: 'info', confirmButtonText: 'Entendido' });
                return;
            }

            this.isSavingForm = true;
            const data = {
                sucursal_id: this.form.clienteFinal.id,
                cliente_final: this.form.clienteFinal.nombre,
                tipo_gestion: this.form.tipoGestion,
                solicitante: this.form.solicitante,
                cod_metodo_envio_id: this.form.cod_metodo_envio_id,
                departamento: this.form.clienteFinal.departamento_nombre,
                provincia: this.form.clienteFinal.provincia_nombre,
                distrito: this.form.clienteFinal.distrito_nombre,
                ruc: this.form.ruc,
                nombres_apellidos: this.form.nombresApellidos,
                dni: this.form.dni,
                documentos_extras: this.form.documentosExtras,
                observaciones: this.form.observaciones
            };

            try {
                await axios.post('datos-agente/solicitud', data);
                const idsAntes = new Set(this.solicitudes.map(s => s.id));
                await this.getDatosAgente();
                const nueva = this.solicitudes.find(s => !idsAntes.has(s.id));
                this.idRecienCreado = nueva ? nueva.id : null;
                this.pagina = 1;

                this.form = { ...formularioVacio(), solicitante: this.nombreUsuario() };
                this.limpiarEmpresa();

                await Swal.fire({
                    title: 'Solicitud enviada',
                    text: 'Te responderemos por correo, normalmente en menos de una hora. Puedes seguir su estado en el historial.',
                    icon: 'success',
                    confirmButtonText: 'Ver historial'
                });
                this.$refs.historial && this.$refs.historial.scrollIntoView({ behavior: 'smooth', block: 'start' });
            } catch (error) {
                Swal.fire({ title: 'No se pudo enviar', text: this.mensajeError(error, 'Ocurrió un problema al guardar la solicitud. Intenta nuevamente.'), icon: 'error', confirmButtonText: 'Aceptar' });
            } finally {
                this.isSavingForm = false;
            }
        },
        async eliminarSolicitud(item) {
            if (this.isDeletingSolicitudId) return;
            const confirmacion = await Swal.fire({
                title: '¿Eliminar la solicitud?',
                text: 'Se eliminarán también su respuesta y sus archivos. No se puede deshacer.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });
            if (!confirmacion.isConfirmed) return;
            this.isDeletingSolicitudId = item.id;
            try {
                await axios.delete(`/datos-agente/solicitud/${item.id}`);
                this.solicitudes = this.solicitudes.filter(s => s.id !== item.id);
                Swal.fire({ title: 'Eliminada', text: 'La solicitud fue eliminada.', icon: 'success', confirmButtonText: 'Aceptar' });
            } catch (error) {
                Swal.fire({ title: 'Error', text: this.mensajeError(error, 'No se pudo eliminar la solicitud.'), icon: 'error', confirmButtonText: 'Aceptar' });
            } finally {
                this.isDeletingSolicitudId = null;
            }
        },

        // ── Respuesta ──
        async abrirModalRespuesta(item) {
            if (this.isLoadingRespuesta) return;
            this.isLoadingRespuesta = true;
            this.solicitudSeleccionada = item;
            this.archivos = [];
            this.nuevoArchivo = { archivo: null, nombre: '' };
            try {
                const response = await axios.get('datos-agente/solicitud/' + item.id);
                this.respuesta = response.data || {};
                this.showRespuestaModal = true;
                await this.getDatosAgenteArchivo(item.id);
            } catch (error) {
                Swal.fire({ title: 'Error', text: 'No se pudo abrir la solicitud.', icon: 'error', confirmButtonText: 'Aceptar' });
            } finally {
                this.isLoadingRespuesta = false;
            }
        },
        async getDatosAgenteArchivo(id) {
            try {
                const response = await axios.get("/getArchivosAgente/" + id);
                this.archivos = response.data || [];
            } catch (error) {
                this.archivos = [];
            }
        },
        async guardarRespuesta() {
            if (this.isSavingRespuesta) return;
            this.isSavingRespuesta = true;
            try {
                await axios.post("/datos-agente/solicitud/" + this.solicitudSeleccionada.id + "/respuesta", {
                    nombres_apellidos: this.respuesta.nombres_apellidos,
                    dni: this.respuesta.dni
                });
                await this.getDatosAgente();
                this.cerrarModalRespuesta();
                Swal.fire({ title: 'Respuesta enviada', text: 'Le avisamos al solicitante por correo.', icon: 'success', confirmButtonText: 'Aceptar' });
            } catch (error) {
                Swal.fire({ title: 'No se pudo guardar', text: this.mensajeError(error, 'Revisa los datos e intenta nuevamente.'), icon: 'error', confirmButtonText: 'Aceptar' });
            } finally {
                this.isSavingRespuesta = false;
            }
        },
        cerrarModalRespuesta() {
            this.showRespuestaModal = false;
            this.archivos = [];
            this.nuevoArchivo = { archivo: null, nombre: '' };
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
        },

        // ── Archivos ──
        seleccionarArchivo(event) {
            const file = event.target.files[0];
            this.nuevoArchivo = file ? { archivo: file, nombre: file.name } : { archivo: null, nombre: '' };
        },
        async subirArchivo() {
            if (this.isUploadingArchivo || !this.nuevoArchivo.archivo) return;
            this.isUploadingArchivo = true;
            try {
                const formData = new FormData();
                formData.append('archivo', this.nuevoArchivo.archivo);
                formData.append('solicitud_id', this.solicitudSeleccionada.id);
                const response = await axios.post('/subir-archivo-agente', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                if (response.data) {
                    this.archivos.push(response.data);
                    this.nuevoArchivo = { archivo: null, nombre: '' };
                    if (this.$refs.fileInput) this.$refs.fileInput.value = '';
                }
            } catch (error) {
                Swal.fire({ title: 'Error', text: this.mensajeError(error, 'No se pudo subir el archivo.'), icon: 'error', confirmButtonText: 'Aceptar' });
            } finally {
                this.isUploadingArchivo = false;
            }
        },
        verArchivo(archivo) {
            // Mismo servidor que el API (en local usa el backend local, en producción el de producción)
            const base = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '');
            window.open(`${base}/storage/${archivo.ruta}`, '_blank');
        },
        async eliminarArchivo(id, index) {
            if (this.isDeletingArchivoId) return;
            const confirmacion = await Swal.fire({
                title: '¿Eliminar el archivo?',
                text: 'No se puede deshacer.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar'
            });
            if (!confirmacion.isConfirmed) return;
            this.isDeletingArchivoId = id;
            try {
                await axios.delete(`/datos-agente/archivo/${id}`);
                this.archivos.splice(index, 1);
            } catch (error) {
                Swal.fire({ title: 'Error', text: this.mensajeError(error, 'No se pudo eliminar el archivo.'), icon: 'error', confirmButtonText: 'Aceptar' });
            } finally {
                this.isDeletingArchivoId = null;
            }
        },
    }
};
</script>

<style scoped>
/* ── Etiquetas de tipo (tonales) ── */
.badge-tipo-merc { background: #fff3e0; color: #e65100; }
.badge-tipo-doc  { background: #e3f2fd; color: #1565c0; }

/* ── Tarjeta del formulario (misma línea que .filter-card) ── */
.agente-card {
    background: #fff;
    border: 1px solid #e8eaf6;
    border-radius: 10px;
    box-shadow: 0 1px 6px rgba(63, 81, 181, 0.06);
}
.agente-card-header { padding: 14px 20px; border-bottom: 1px solid #eef0f7; }
.agente-card-title, .seccion-titulo {
    font-size: 0.92rem;
    font-weight: 700;
    color: #1f2340;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}
.agente-card-title .bi, .seccion-titulo .bi { color: #3f51b5; }
.seccion-titulo { margin-bottom: 10px; }
.agente-card-body { padding: 20px; }

/* ── Formulario ── */
.agente-form {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px 24px;
}
@media (max-width: 767.98px) {
    .agente-form { grid-template-columns: 1fr; }
}
.form-block { display: flex; flex-direction: column; gap: 6px; }
.form-block.full { grid-column: 1 / -1; }
.form-lbl {
    font-size: 0.7rem;
    font-weight: 600;
    color: #5b6078;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin: 0;
}
.req { color: #c62828; }
.form-help { font-size: 0.75rem; color: #6b7085; }
.form-help-error { color: #c62828; }
.form-readonly { background: #f7f8fc !important; color: #5b6078 !important; }
/* Fila (no columna como los demás bloques): el botón conserva su tamaño y va a la derecha */
.form-block.form-submit { flex-direction: row; justify-content: flex-end; align-items: center; border-top: 1px solid #eef0f7; padding-top: 14px; }

/* ── RUC con botones ── */
.input-with-actions { display: flex; gap: 6px; align-items: center; }
.input-with-actions .form-control { flex: 1; }
.fab-btn {
    width: 34px; height: 34px;
    border-radius: 8px;
    border: none;
    display: flex; align-items: center; justify-content: center;
    font-size: 0.9rem;
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color 0.15s ease;
}
.fab-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.fab-search { background: #3f51b5; color: #fff; }
.fab-search:hover:not(:disabled) { background: #303f9f; }
.fab-add { background: #e8eaf6; color: #3f51b5; }
.fab-add:hover { background: #c5cae9; }

/* ── Chips de selección ── */
.tipo-gestion-chips, .requisitos-wrap { display: flex; gap: 8px; flex-wrap: wrap; }
.tipo-chip, .req-chip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    border: 1px solid #dfe2ef;
    border-radius: 8px;
    background: #fff;
    cursor: pointer;
    font-size: 0.82rem;
    font-weight: 500;
    color: #4a4f66;
    transition: border-color 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}
.tipo-chip input, .req-chip input { position: absolute; opacity: 0; pointer-events: none; }
.tipo-chip:hover, .req-chip:hover { border-color: #9fa8da; color: #3f51b5; }
.tipo-chip.active, .req-chip.active { border-color: #3f51b5; background: #f3f4fb; color: #3f51b5; font-weight: 600; }
.tipo-chip:focus-within, .req-chip:focus-within { box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.2); }

/* ── Botón enviar ── */
.btn-guardar {
    background: #3f51b5;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 9px 22px;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: background-color 0.15s ease;
}
.btn-guardar:hover:not(:disabled) { background: #303f9f; }
.btn-guardar:disabled { opacity: 0.6; cursor: not-allowed; }

/* ── Historial ── */
.fila-nueva > td { animation: resaltar 2.5s ease-out; }
@keyframes resaltar { from { background-color: #e8f5e9; } to { background-color: transparent; } }

/* ── Modal ── */
.solicitud-resumen {
    background: #f7f8fc;
    border: 1px solid #e8eaf6;
    border-radius: 10px;
    padding: 14px 18px;
}
.solicitud-resumen-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px 16px;
}
.resumen-item { display: flex; flex-direction: column; gap: 2px; }
.resumen-full { grid-column: 1 / -1; }
.resumen-label { font-size: 0.66rem; font-weight: 600; text-transform: uppercase; color: #8a8fa3; letter-spacing: 0.04em; }
.resumen-val { font-size: 0.84rem; color: #2e3247; font-weight: 500; }
.seccion-titulo-modal {
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #3f51b5;
    border-bottom: 1px solid #e8eaf6;
    padding-bottom: 6px;
}
.aviso-pendiente {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background: #fff8e1;
    border: 1px solid #ffe082;
    color: #6d4c00;
    border-radius: 10px;
    padding: 14px 16px;
    font-size: 0.85rem;
}
.aviso-pendiente .bi { font-size: 1.2rem; }
.archivo-upload-row { display: flex; gap: 10px; align-items: center; }
.archivo-upload-row .form-control { flex: 1; }
</style>
